import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

const linkClass = (active, justifyCenter) =>
  cn(
    'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors relative w-full',
    justifyCenter && 'justify-center',
    active ? 'bg-white/5 text-primary' : 'text-muted hover:text-white hover:bg-white/[0.03]',
  );

const isPathActive = (item, pathname) => {
  if (!item?.path) return false;
  const path = `/${item.path}`;
  if (item.exact) return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
};

const NavItem = ({ item, collapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const childActive = hasChildren && item.children.some(child => isPathActive(child, location.pathname));
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  if (hasChildren) {
    const Icon = item.icon;
    return (
      <li>
        <button
          type="button"
          title={collapsed ? item.name : undefined}
          onClick={() => {
            if (collapsed && item.children[0]?.path) {
              navigate(`/${item.children[0].path}`);
              return;
            }
            setOpen(v => !v);
          }}
          className={linkClass(childActive, collapsed)}>
          {childActive ? (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r" />
          ) : null}
          <Icon className="w-4 h-4 flex-shrink-0" />
          {!collapsed && (
            <>
              <span className="truncate text-left">{item.name}</span>
              {item.badge ? (
                <span className="flex-shrink-0 px-1.5 py-px rounded text-[9px] font-semibold uppercase tracking-wide bg-primary/15 text-primary">
                  {item.badge}
                </span>
              ) : null}
              <ChevronDown
                className={cn('w-3.5 h-3.5 flex-shrink-0 ml-auto transition-transform', open && 'rotate-180')}
              />
            </>
          )}
        </button>
        {!collapsed && open ? (
          <ul className="mt-0.5 ml-3 pl-3 border-l border-shell-border space-y-0.5">
            {item.children.map(child => {
              const active = isPathActive(child, location.pathname);
              const ChildIcon = child.icon;
              return (
                <li key={child.path}>
                  <NavLink to={`/${child.path}`} className={linkClass(active, false)}>
                    {active ? (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary rounded-r" />
                    ) : null}
                    {ChildIcon ? <ChildIcon className="w-3.5 h-3.5 flex-shrink-0" /> : null}
                    <span className="truncate">{child.name}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        ) : null}
      </li>
    );
  }

  const active = isPathActive(item, location.pathname);
  const Icon = item.icon;

  return (
    <li>
      <NavLink
        to={`/${item.path}`}
        title={collapsed ? item.name : undefined}
        className={linkClass(active, collapsed)}>
        {active ? (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r" />
        ) : null}
        <Icon className="w-4 h-4 flex-shrink-0" />
        {!collapsed && <span className="truncate">{item.name}</span>}
        {!collapsed && item.badge ? (
          <span className="flex-shrink-0 ml-auto px-1.5 py-px rounded text-[9px] font-semibold uppercase tracking-wide bg-primary/15 text-primary">
            {item.badge}
          </span>
        ) : null}
      </NavLink>
    </li>
  );
};

export default NavItem;
